import { useEffect, useId, useRef, useState } from 'react';
import { Input } from '@/components/Input';
import type { PlaceSuggestion } from '@/lib/divorce/places';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPick?: (suggestion: PlaceSuggestion) => void;
  getSuggestions: (query: string, signal: AbortSignal) => PlaceSuggestion[] | Promise<PlaceSuggestion[]>;
  autoComplete?: string;
  minChars?: number;
  placeholder?: string;
};

export function SuggestInput({
  label,
  value,
  onChange,
  onPick,
  getSuggestions,
  autoComplete = 'off',
  minChars = 1,
  placeholder,
}: Props) {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [items, setItems] = useState<PlaceSuggestion[]>([]);

  useEffect(() => {
    const q = value.trim();
    if (q.length < minChars) {
      setItems([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const next = await getSuggestions(q, controller.signal);
        if (!controller.signal.aborted) {
          setItems(next);
          setHighlight(0);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (!controller.signal.aborted) setItems([]);
      }
    }, 220);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [value, getSuggestions, minChars]);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showList = open && items.length > 0;

  const pick = (item: PlaceSuggestion) => {
    onPick?.(item);
    setOpen(false);
    setItems([]);
  };

  return (
    <div ref={wrapRef} className='relative w-full'>
      <Input
        label={label}
        value={value}
        autoComplete={autoComplete}
        placeholder={placeholder}
        role='combobox'
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete='list'
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (!showList) return;
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlight((i) => (i + 1) % items.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight((i) => (i - 1 + items.length) % items.length);
          } else if (event.key === 'Enter') {
            event.preventDefault();
            pick(items[highlight]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {showList ? (
        <ul
          id={listId}
          role='listbox'
          className='absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg'
        >
          {items.map((item, index) => (
            <li key={item.id} role='option' aria-selected={index === highlight}>
              <button
                type='button'
                className={`w-full px-4 py-2.5 text-left text-sm ${
                  index === highlight ? 'bg-primary/10 text-gray-900' : 'text-gray-800'
                }`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(item)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
